import { z } from "zod";
import { productInformationSchema } from "@utils/productInformation";

const requiredPositiveDecimal = (
  label: string,
  maxValue: number,
  maxMessage: string,
) =>
  z
    .number({
      invalid_type_error: `${label}를 입력해주세요.`,
      required_error: `${label}를 입력해주세요.`,
    })
    .refine((value) => Number.isFinite(value) && value > 0, {
      message: `${label}를 입력해주세요.`,
    })
    .refine((value) => value <= maxValue, {
      message: maxMessage,
    });

export const emailSchema = z.object({
  login_id: z.string().email("올바른 이메일 형식이 아닙니다."),
});

export const joinSchema = () =>
  z
    .object({
      name: z.string().min(1, ""),
      country: z.string().min(1, ""),
      nickname: z.string().min(1, "").max(100, "").regex(/^[^!@#$%^&*()_+=\-[\]{};"'|\\/<>,.?~`]+$/, ""),
      isNicknameUnique: z.boolean(),
      password: z
        .string()
        .min(8)
        .max(20)
        .regex(/^(?=.*[a-zA-Z])(?=.*\d)/),
      passwordConfirm: z.string(),
    })
    .passthrough()
    .refine(
      (data) => {
        return data.password === data.passwordConfirm;
      },
      {
        message: "",
        path: ["passwordConfirm"],
      },
    );

export const loginSchema = z.object({
  login_id: z.string().email("이메일 형식이 맞지 않습니다."),
  password: z.string(),
});

export const passwordSchema = z
  .object({
    findemail: z.string().email("올바른 이메일 형식이 아닙니다."),
    newpassword: z
      .string()
      .min(8, "")
      .max(20, "")
      .regex(/^(?=.*[a-zA-Z])(?=.*\d)/, ""),
    newpasswordConfirm: z.string(),
  })
  .refine((data) => data.newpassword === data.newpasswordConfirm, {
    message: "",
    path: ["newpasswordConfirm"],
  });

export const profileEditSchema = z
  .object({
    name: z.string().min(1, ""),
    country: z.string().min(1, ""),
    // postcode: z.string().min(1, ""),
    // address: z.string().min(1, ""),
    nickname: z.string().min(1, "").max(100, "").regex(/^[^!@#$%^&*()_+=\-[\]{};"'|\\/<>,.?~`]+$/, "")
    .optional(),
  })
  .passthrough();

const colorStockSchema = z.object({
  color_id: z.number().min(1).nullable().optional(),
  option_key: z.string().min(1),
  color_name_ko: z.string().nullable().optional(),
  color_name_en: z.string().nullable().optional(),
  color_name_ja: z.string().nullable().optional(),
  color_sku: z.string().max(100).nullable().optional(),
  hex_code: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable().optional(),
  base_color_id: z.number().min(1).nullable().optional(),
  size_list: z.array(z.object({
    size_id: z.number().min(1, "색상에 해당하는 사이즈를 선택해주시기 바랍니다."),
    amount: z.number().min(1, "수량을 선택해주시기 바랍니다."),
    size_sku: z.string().max(100).nullable().optional(),
  })).nonempty("색상에 해당하는 사이즈를 선택해주시기 바랍니다."),
}).refine(
  (value) => Boolean(value.color_id) ||
    (Boolean(value.color_name_ko || value.color_name_en) && Boolean(value.hex_code)),
  "직접 색상은 색상명과 HEX가 필요합니다.",
);

  export const productSchema = () =>z
  .object({
    ...productInformationSchema.shape,
    name_ko: z.string().min(1, "상품명을 입력해주세요."),
    name_en: z.string().min(1, "영문 상품명을 입력해주세요."),

    stock_list: z
    .array(colorStockSchema)
    .nonempty(""), // 색상에러문구 
    audience_category_ids: z.array(z.number()).min(1, "앱 대분류를 선택해주세요."),
    patterns: z.array(z.object({
      tag_id: z.number().nullable().optional(),
      name_ko: z.string().nullable().optional(),
      name_en: z.string().nullable().optional(),
      name_ja: z.string().nullable().optional(),
    })).min(1, "패턴을 선택하거나 입력해주세요."),
    
    brand_id: z.number().nullable().refine(val => val !== null && val !== 0, "브랜드를 선택해주세요."),
    description_ko: z.string().min(1, "상품 설명을 입력해주세요."),
    image_list: z.array(z.string()).nonempty("상품 사진을 등록해주세요."),
    product_weight_kg: requiredPositiveDecimal(
      "상품무게",
      100,
      "상품무게는 100kg 이하로 입력해주세요.",
    ),
    shipping_weight_kg: requiredPositiveDecimal(
      "배송무게",
      100,
      "배송무게는 100kg 이하로 입력해주세요.",
    ),
    volume_width_cm: requiredPositiveDecimal(
      "부피 가로",
      999.99,
      "가로는 999.99cm 이하로 입력해주세요.",
    ),
    volume_depth_cm: requiredPositiveDecimal(
      "부피 세로",
      999.99,
      "세로는 999.99cm 이하로 입력해주세요.",
    ),
    volume_height_cm: requiredPositiveDecimal(
      "부피 높이",
      999.99,
      "높이는 999.99cm 이하로 입력해주세요.",
    ),
    price: z.number().min(1000,"판매가격을 입력해주세요."),
    terms: z.boolean().refine((val) => val === true, "샤카라카 이용약관에 동의해주세요."),
  })
  .passthrough();

  
  export const productEditSchema =  z
  .object({
    name_ko: z.string().min(1, "상품명을 입력해주세요."),
    brand_id: z.number().nullable().refine(val => val !== null && val !== 0, "브랜드를 선택해주세요."),
    product_condition: z.string().min(1, "상품의 컨디션을 선택해주세요."),
    price: z.number().min(1000,"판매가격을 입력해주세요."),
    description_ko: z.string().min(1, "상품 설명을 입력해주세요."),
    image_list: z.array(z.string()).nonempty("상품 사진을 등록해주세요."),
    
    stock_list: z
    .array(colorStockSchema)
    .nonempty(""), // 색상에러문구 
  })
  .passthrough();



  export const stockRegisterSchema = () =>z
  .object({
    name_ko: z.string().min(1, "상품명을 입력해주세요."),
    stock_list: z.array(colorStockSchema).nonempty() // stock_list 빈 배열 허용하지 않음
  })
  .passthrough();


export const stockEditSchema = z
  .object({
    name_ko: z.string().min(1, "상품명을 입력해주세요."),
    name_en: z.string().min(1, "상품명(영문)을 입력해주세요."),
      stock_list: z
      .array(colorStockSchema)
      .nonempty(""), // 색상에러문구 
  })
  .passthrough();

export const aiProductSchema = () =>
    z
      .object({
        category: z.number().nullable().optional(),
        depth1: z.number().nullable().optional(),
        depth2: z.number().nullable().optional(), 
        tag_type: z.array(z.number()).max(1, "타입은 1개만 선택할 수 있습니다."),
        tag_style: z.array(z.number()).min(1, "스타일을 선택해주세요.").max(2, "스타일은 최대 2개까지 선택 가능합니다."),
        tag_fit: z.array(z.number()).min(1, "실루엣/핏을 선택해주세요.").max(2, "실루엣/핏은 최대 2개까지 선택 가능합니다."),
        tag_occasion: z.array(z.number()).min(1, "큐레이션을 선택해주세요.").max(2, "큐레이션은 최대 2개까지 선택 가능합니다."),
        gender: z.string().min(1, "성별을 선택해주세요."),
        audience_category_ids: z.array(z.number()).min(1, "앱 대분류를 선택해주세요."),
        color: z.object({
          color_id: z.number().min(1).nullable().optional(),
          option_key: z.string().optional(),
          color_name_ko: z.string().nullable().optional(),
          color_name_en: z.string().nullable().optional(),
          color_name_ja: z.string().nullable().optional(),
          hex_code: z.string().nullable().optional(),
        }).refine((value) => Boolean(value.color_id) || (Boolean(value.color_name_en?.trim()) && /^#[0-9A-Fa-f]{6}$/.test(value.hex_code || "")), "직접 색상은 영문 색상명과 올바른 HEX가 필요합니다."),
        pattern: z.object({
          pattern_id: z.number().nullable().optional(), 
          pattern_name: z.string().min(1, "패턴은 필수입니다."),
          name_ko: z.string().nullable().optional(),
          name_en: z.string().nullable().optional(),
          name_ja: z.string().nullable().optional(),
        }).refine((value) => Boolean(value.pattern_id) || [value.name_ko, value.name_en, value.name_ja].every((name) => Boolean(name?.trim())), "직접 패턴명은 한글·영문·일문 모두 필요합니다."),
      })
      .refine(
        (data) => data.tag_type.length === 1,
        {
          message: "타입을 선택해주세요.",
          path: ["tag_type"],
        }
      );
  
  
export const changePasswordSchema = z
  .object({
    current_password: z.string(),
    new_password: z
      .string()
      .min(8)
      .max(20)
      .regex(/^(?=.*[a-zA-Z])(?=.*\d)/),
    confirm_password: z.string(),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "비밀번호 재확인",
    path: ["confirm_password"],
  });


// 리뷰 작성
export const reviewSchema = z
  .object({
    rate: z.number().min(1, ""), 
    review_text: z.string().min(1, ""),
  })
  .passthrough();

  // 판매 정책 설정
  export const salesPolicySchema = z 
  .object (
    {
      introduction: z.string().min(1,""),
      // shipping_fee: z.number().min(1, ""), 
      free_shipping_fee: z.number().min(1, ""), 
    }
  )
  .passthrough();

  // 계좌관리
  export const bankAccountSchema = z
  .object({
    account_holder: z.string().min(1, ""),
    bank_cd: z.string().min(1, ""),
    account_number: z.string().min(1, "계좌번호를 입력해주세요."),
  })
  .passthrough();
